/**
 * 20261028 학생 송출 종료 정리 실행 (web-relay R-224, milestone_42 §3.14.3·R-234)
 *
 * 자원 그룹마다 owner 검사를 먼저 하고, 그룹 안 항목은 하나가 실패해도 나머지를 계속한다.
 * 실패 객체도 참조는 부르는 쪽이 null 로 바꿔 다음 owner 가 재사용하지 않게 한다(해제 성공으로 세지 않는다).
 * 판단만 한다 — 전역도 import 도 없다.
 */

/**
 * 항목들을 모두 시도한다. 실패가 있으면 마지막에 모아 던진다.
 *
 * @param {Array<Function>} items
 */
export const runEach = async (items) => {
    const errors = [];
    for (let i = 0; i < items.length; i++) {
        try {
            await items[i]();
        } catch (err) {
            errors.push(err);
        }
    }
    if (errors.length > 0) {
        const err = new Error(`cleanup-item-failed:${errors.length}`);
        err.errors = errors;
        throw err;
    }
};

/** 트랙 정지(이미 끝났으면 그냥 둔다) */
export const stopTrack = (track) => {
    if (track !== undefined && track !== null && track.readyState !== "ended" && typeof track.stop === "function") {
        track.stop();
    }
};

/** Producer/transport 닫기(이미 닫혔으면 그냥 둔다) */
export const closeObject = (obj) => {
    if (obj !== undefined && obj !== null && !obj.closed && typeof obj.close === "function") {
        obj.close();
    }
};

/** 20261028 기존 입력 정리 함수의 false 반환도 해제 실패로 수집한다 (R-242). */
const clearInputOrFail = async (name, clear) => {
    if ((await clear()) === false) throw new Error(`cleanup-input-failed:${name}`);
};

/**
 * @param {Array<{name: String, isOwner: Function, run: Function}>} groups
 * @param {Function} [log]
 * @returns {Promise<Array<{name: String, err: Error}>>} 실패 목록
 */
export const runCleanupGroups = async (groups, log = (msg) => console.log(msg)) => {
    const failures = [];
    for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        let isOwner = false;
        try {
            isOwner = group.isOwner() === true;
        } catch (err) {
            isOwner = false;
        }
        if (!isOwner) {
            // 순서화 때문에 일어나면 안 된다. 그래도 일어나면 정지하지 않고 남긴다.
            log(`sendExitCleanup - [${group.name}] 이 종료 작업의 owner 가 아니다. 건드리지 않는다.`);
            failures.push({ name: group.name, err: new Error("not-owner") });
            continue;
        }
        try {
            await group.run();
        } catch (err) {
            log(`sendExitCleanup - [${group.name}] 정리 실패 ${err && err.message}`);
            failures.push({ name: group.name, err });
        }
    }
    return failures;
};

/**
 * MediaSoupClient 모듈 상태를 ctx 로 받아 종료 시작/정리 훅을 만든다.
 *
 * @param {Object} guard - createSendExitGuard()
 * @param {Object} ctx
 * @param {Object}   ctx.clientInfo
 * @param {Function} ctx.stopRecovery       - (why) 재생성 감시·ICE 재시작·Android 어댑터 중단
 * @param {Function} ctx.takePendingRestore - () → {audioTrack, videoTrack} | null, 비운다
 * @param {Function} [ctx.hasPendingRestore] - 웹 송출 재생성이 트랙을 보관 중인지 확인 (20261028 R-241)
 * @param {Function} ctx.traceAudio         - (producer) 기존 audio 종료 계측
 * @param {Function} ctx.notifyCameraStopped
 * @param {Function} ctx.notifyRotationOff
 * @param {Function} ctx.clearAudioInput
 * @param {Function} ctx.clearVideoInput
 * @param {Function} ctx.isNativeWebView    - Android/iOS 앱 웹뷰면 true(기존 native·unpublishAfter 종료 경로 유지)
 * @param {Function} [ctx.snapshotExitInputs] - (token) 믹서 입력·마이캠 종료 스냅샷 (20261029 R-257)
 * @param {Function} [ctx.releaseExitInputs]  - (token, skipMyCam) → failures[]: 스냅샷 전체 반환 (20261029 R-257)
 * @param {Function} [ctx.endExitInputs]      - (token) 스냅샷 닫기 (20261029 R-258)
 * @param {Function} [log]
 */
export const createSendExitHooks = (guard, ctx, log = (msg) => console.log(msg)) => {
    /** 동기. 첫 await 전에 복구 의도를 멈춘다. */
    const onBegin = (token) => {
        ctx.stopRecovery(`send-exit:${token.why}`);
        if (typeof ctx.snapshotExitInputs === "function") ctx.snapshotExitInputs(token); // 20261029 캡처 입력 스냅샷 (R-257)
        log(`sendExitCleanup - 송출 복구 의도를 멈췄다(${token.why}).`);
    };

    /** 자원 그룹 정리. 실패 목록을 돌려준다. */
    const onFinish = async (token) => {
        const ci = ctx.clientInfo;
        // 20261028 SDK transport가 없는 웹 생성/복원 대기는 정리해 다음 공유를 허용한다 (R-241).
        // 이미 null인 transport는 그대로이므로 늦은 native/web 응답 분기를 바꾸지 않는다.
        const pendingWebSend = ci.sendTransport === null &&
            (ci.sendTransportStatus === "transport" || (typeof ctx.hasPendingRestore === "function" && ctx.hasPendingRestore()));
        if (ctx.isNativeWebView() && !pendingWebSend) {
            log("sendExitCleanup - 앱 웹뷰는 기존 native 종료·unpublishAfter 경로를 쓴다. 웹 자원 정리는 하지 않는다.");
            return typeof ctx.releaseExitInputs === "function" ? ctx.releaseExitInputs(token, true) : []; // 20261029 웹뷰는 믹서 입력이 없고 마이캠은 기존 종료에 맡긴다 (R-257)
        }
        const pi = ci.produceInfo;
        const isOwner = () => guard.isExitingToken(token);

        return await runCleanupGroups([
            {
                name: "send-tracks",
                isOwner,
                run: () => {
                    const kept = ctx.takePendingRestore();
                    return runEach([
                        () => { try { stopTrack(pi.audioTrack); } finally { pi.audioTrack = null; } },
                        () => { try { stopTrack(pi.videoTrack); } finally { pi.videoTrack = null; } },
                        () => { if (kept) stopTrack(kept.audioTrack); },
                        () => { if (kept) stopTrack(kept.videoTrack); }
                    ]);
                }
            },
            {
                name: "producers",
                isOwner,
                run: () => runEach([
                    () => {
                        try {
                            if (pi.audioProducer && !pi.audioProducer.closed) ctx.traceAudio(pi.audioProducer);
                            closeObject(pi.audioProducer);
                        } finally {
                            pi.audioProducer = null;
                        }
                    },
                    () => { try { closeObject(pi.videoProducer); } finally { pi.videoProducer = null; } }
                ])
            },
            {
                name: "transport",
                isOwner,
                run: () => runEach([
                    () => { try { closeObject(ci.sendTransport); } finally { ci.sendTransport = null; } }
                ])
            },
            {
                name: "status",
                isOwner,
                run: () => {
                    // 다음 시작의 선행 조건: sendTransportStatus === "ended" && sendTransport === null
                    ci.sendTransportStatus = "ended";
                    pi.audioStatus = "ended";
                    pi.videoStatus = "ended";
                    ci.sendTransportCallback = null;
                    ci.sendTransportErrback = null;
                    pi.produceCallback = null;
                    pi.produceErrback = null;
                }
            },
            {
                name: "display",
                isOwner,
                run: () => runEach([
                    () => {
                        if (pi.videoSource === "camera") {
                            pi.videoSource = null;
                            ctx.notifyCameraStopped();
                        }
                    },
                    () => ctx.notifyRotationOff()
                ])
            },
            {
                name: "inputs",
                isOwner,
                run: () => runEach([
                    () => clearInputOrFail("audio", ctx.clearAudioInput), // 20261028 false도 실패 (R-242)
                    () => clearInputOrFail("video", ctx.clearVideoInput)
                ])
            },
            {
                // 20261029 스냅샷의 믹서 입력 clone(덮어써진 이전 쌍 포함)·마이캠 캡처 반환 (R-248·R-250·R-253·R-257)
                name: "capture-inputs",
                isOwner,
                run: () => {
                    const failures = typeof ctx.releaseExitInputs === "function" ? ctx.releaseExitInputs(token, ctx.isNativeWebView()) : [];
                    if (failures.length > 0) {
                        const err = new Error(`capture-input-failed:${failures.length}`);
                        err.errors = failures;
                        throw err;
                    }
                }
            }
        ], log);
    };

    // 20261029 조기 finish 이후 본문·획득 대기의 실패도 모으고, 보류 시작 직전에만 닫는다 (R-259).
    const onSettled = (token) => {
        try {
            if (!guard.isExitingToken(token)) return [{ name: "capture-inputs", err: new Error("not-owner") }];
            return typeof ctx.releaseExitInputs === "function" ? ctx.releaseExitInputs(token, ctx.isNativeWebView()) : [];
        } finally {
            if (typeof ctx.endExitInputs === "function") ctx.endExitInputs(token);
        }
    };

    return { onBegin, onFinish, onSettled };
};
